const COOKIE_HEADING_TEXTS = new Set([
  'Cookie Settings',
  'Strictly Necessary Cookies',
  'Functional Cookies',
  'Performance Cookies',
  'Advertising and Social Media Cookies',
  'Cookie List',
]);

const ONETRUST_ROOT_SELECTORS = [
  '#onetrust-banner-sdk',
  '#onetrust-pc-sdk',
  '.ot-sdk-container',
  '.ot-sdk-show-settings',
];

const SANITIZER_IGNORE_ROOT_SELECTORS = [
  '.awplayer',
  '[data-awplayer]',
  '[class*="awp"]',
  '[class*="awplayer"]',
  'awplayer-app',
  '.ab-player',
];

function getNormalizedHeadingText(heading) {
  return heading.textContent?.replace(/\s+/g, ' ').trim() ?? '';
}

/**
 * Skip third-party/player roots where headings can be placeholders for runtime rendering.
 *
 * @param {HTMLElement} element
 */
function isInsideIgnoredSanitizerRoot(element) {
  return SANITIZER_IGNORE_ROOT_SELECTORS.some((selector) => element.closest(selector));
}

/**
 * Remove truly empty heading tags anywhere in the document.
 *
 * @param {ParentNode} root
 */
function removeEmptyHeadings(root = document) {
  for (const heading of root.querySelectorAll('h1, h2, h3, h4, h5, h6')) {
    if (!(heading instanceof HTMLElement)) continue;
    if (isInsideIgnoredSanitizerRoot(heading)) continue;

    const text = getNormalizedHeadingText(heading);

    if (!text) {
      heading.remove();
    }
  }
}

/**
 * Replace a heading tag with a paragraph while keeping its attributes and children.
 *
 * @param {HTMLElement} heading
 */
function replaceHeadingWithParagraph(heading) {
  if (!(heading instanceof HTMLElement) || heading.dataset.seoHeadingSanitized === 'true') return;

  const paragraph = document.createElement('p');

  for (const { name, value } of Array.from(heading.attributes)) {
    paragraph.setAttribute(name, value);
  }

  paragraph.dataset.seoHeadingSanitized = 'true';
  paragraph.replaceChildren(...Array.from(heading.childNodes));
  heading.replaceWith(paragraph);
}

/**
 * Sanitize unnecessary OneTrust headings so they do not appear in SEO heading checks.
 *
 * @param {ParentNode} root
 */
function sanitizeOneTrustHeadings(root = document) {
  for (const selector of ONETRUST_ROOT_SELECTORS) {
    for (const container of root.querySelectorAll(selector)) {
      if (!(container instanceof HTMLElement)) continue;

      for (const heading of container.querySelectorAll('h1, h2, h3, h4, h5, h6')) {
        if (!(heading instanceof HTMLElement)) continue;
        if (isInsideIgnoredSanitizerRoot(heading)) continue;

        const text = getNormalizedHeadingText(heading);

        if (!text) {
          heading.remove();
          continue;
        }

        if (COOKIE_HEADING_TEXTS.has(text)) {
          replaceHeadingWithParagraph(heading);
        }
      }
    }
  }
}

function initSeoHeadingSanitizer() {
  removeEmptyHeadings();
  sanitizeOneTrustHeadings();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        removeEmptyHeadings(node);
        removeEmptyHeadings(node.parentElement ?? document);
        sanitizeOneTrustHeadings(node);
        sanitizeOneTrustHeadings(node.parentElement ?? document);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSeoHeadingSanitizer, { once: true });
} else {
  initSeoHeadingSanitizer();
}
